"use client";
import React from 'react';
import { Card, CardHeader, Divider, CardBody, Link, Button } from "@heroui/react";
import { SocialIcon } from 'react-social-icons';
import { CheckCircle2, Lock } from 'lucide-react';
import {  useRouter } from 'next/navigation';

const ContactInformation = ({ isSignedIn, data, InputData }: { isSignedIn: boolean; data: any; InputData: any | null }) => {
    const { company } = data;
    const company_contact = company?.company_contact || {};
    const inputData = InputData || {};
    const router = useRouter();
    const useInputData = company.verified;

    const social_medias = useInputData ? inputData.company_input_social_media : company_contact.social_medias || [];
    const emails = useInputData ? inputData.company_input_email : company_contact.emails || [];
    const contacts = useInputData ? inputData.company_input_contact : company_contact.contacts || [];
    const websites = useInputData ? inputData.company_input_website : (company.company_websites?.websites || []).map((website: string) => ({ website }));

    const renderContactInfo = (label: string, items: any[]) => (
        <div className="flex flex-col">
            <span className="text-black/80">{label}:</span>
            {label === 'Phone' ? (
                <div className="flex flex-col gap-2 py-1 md:flex-row text-sm">
                    {items.length > 0 ? (
                        items.map((item: any, index: number) =>
                            typeof item === 'string' ? (
                                <span key={index}>{item}</span>
                            ) : item.is_private ? (
                                <span key={index} className="text-blue-500 blur-sm hover:underline">
                                    {label.toLowerCase()}
                                </span>
                            ) : (
                                <span key={index}>{item.contact}</span>
                            )
                        )
                    ) : (
                        <span>No {label.toLowerCase()} available</span>
                    )}
                </div>
            ) : label === 'Email' ? (
                <div className="flex flex-col gap-2 py-1 md:flex-row text-sm">
                    {items.length > 0 ? (
                        items.map((item: any, index: number) =>
                            typeof item === 'string' ? (
                                <span key={index}>{item}</span>
                            ) : item.is_private ? (
                                <span key={index} className="text-blue-500 blur-sm hover:underline">
                                    {label.toLowerCase()}
                                </span>
                            ) : (
                                <span key={index}>{item.email}</span>
                            )
                        )
                    ) : (
                        <span>No {label.toLowerCase()} available</span>
                    )}
                </div>
            ) : label === 'Website' ? (
                <div className="flex flex-col gap-2 py-1 md:flex-row text-sm">
                    {items.length > 0 ? (
                        items.map((item: any, index: number) => (
                            <Link
                                key={index}
                                href={item.website}
                                target="_blank"
                                rel="nofollow noopener noreferrer"
                                showAnchorIcon
                                className="text-black/80 text-sm"
                            >
                                {item.website}
                            </Link>
                        ))
                    ) : (
                        <span>No {label.toLowerCase()} available</span>
                    )}
                </div>
            ) : null}
            <Divider />
        </div>
    );

    const BlurredContent = ({ children }: { children: React.ReactNode }) => {
        if (isSignedIn) {
            return <>{children}</>;
        }
        
        return (
            <div className="relative">
                <div className="blur-sm pointer-events-none">
                    {children}
                </div>
                <div className="absolute inset-0 flex flex-col items-center min-h-fit justify-center bg-black/5 rounded-md">
                    <div className="bg-white/90 p-4 rounded-lg shadow-lg text-center max-w-md">
                        <Lock className="mx-auto mb-2 text-primary h-4 w-4" />
                        <h3 className="text-sm font-semibold mb-1">Content Locked Locked</h3>
                        <p className="mb-2 text-xs">Sign in to view verification status</p>
                        <Button 
                            color="primary" 
                            size="sm"
                            className="text-xs py-1"
                            as="a"
                            href={`/login?redirectUrl=/companies/${company.uen}/${company.slug}`}
                            rel="nofollow"
                            // onPress={() => router.push(`/login?redirectUrl=/companies/${company.uen}/${company.slug}`)}
                        >
                            Sign In
                        </Button>
                    </div>
                </div>  
            </div>
        );
    };

    const renderSocialMediaIcons = (socialMedias: any[]) => (
        <div className="flex flex-col">
            <span className="text-black/80">Social Media:</span>
            <div className="flex flex-row gap-2 py-1">
            {socialMedias.length > 0 ? (
                socialMedias.map((socialMedia: any, index: number) => {
                const handle = socialMedia.handle || socialMedia;
                const network = handle.match(/facebook|twitter|linkedin|instagram|youtube|pinterest|tiktok/)?.[0];
                return network ? <SocialIcon key={index} network={network} url={handle} className="!h-8 !w-8 md:!h-10 md:!w-10" /> : null;
                })
            ) : (
                <span className='text-sm'>No social media available</span>
            )}
            </div>
            {socialMedias.length > 0 && data.company_scrapped_records && (
            <>
                <Divider className="my-2" />
                <BlurredContent>
                <div className="space-y-2 min-h-[150px]">
                    <span className="text-black/80">Verification Status</span>
                    <div className="space-y-1 text-sm">
                    {['facebook', 'instagram'].map((platform) => (
                        data.company_scrapped_records && data.company_scrapped_records[platform] && (
                        <div key={platform} className="flex items-center gap-2">
                            <SocialIcon network={platform} url={data.company_scrapped_records[platform][0]} className='!w-8 !h-8' />
                            {data.company_scrapped_records[`${platform}_verified`] ? (
                            <span className="flex items-center gap-1 text-green-600">
                                <CheckCircle2 className="h-4 w-4" />
                                Verified by {platform.charAt(0).toUpperCase() + platform.slice(1)}
                            </span>
                            ) : (
                            <span className="text-muted-foreground">Not verified by {platform.charAt(0).toUpperCase() + platform.slice(1)}</span>
                            )}
                        </div>
                        )
                    ))}
                    {!data.company_scrapped_records || (!data.company_scrapped_records.facebook && !data.company_scrapped_records.instagram) && (
                        <span className="text-muted-foreground">No verification status available at the moment</span>
                    )}
                    </div>
                </div>
                </BlurredContent>
            </>
            )}
        </div>
    );

    return (
            <Card radius="md">
                <CardHeader className="flex flex-1 gap-3">
                    <h2 className="font-bold ">Contact Information for {company.entity_name}</h2>
                </CardHeader>
                <Divider />
                <CardBody>
                        <div className="flex flex-1 flex-col space-y-2">
                            <div className="flex flex-col">
                                <span className="text-black/80">Address Type:</span>
                                <span className="text-sm">{company.address_type}</span>
                            </div>
                            <Divider />
                            <div className="flex flex-col">
                                <span className="text-black/80">Address:</span>
                                <span>
                                    <Link
                                        isExternal
                                        href={`https://www.google.com/maps/search/${company.block ? company.block + '+' : ''}${
                                            company.street_name !== 'na' ? company.street_name + '+' : ''
                                        }${company.building_name !== 'na' ? company.building_name : ''}`}
                                        target="_blank"
                                        rel="nofollow noopener noreferrer"
                                        showAnchorIcon
                                        className="text-black/80 text-sm"
                                    >
                                        {company.block !== 'na' ? company.block + ', ' : ''}
                                        {company.street_name !== 'na' ? company.street_name : ''}
                                        {company.level_no || company.unit_no
                                            ? `, #${
                                                  company.level_no ? `${company.level_no}${company.unit_no ? `-${company.unit_no}` : ''}` : `${company.unit_no}`
                                              }`
                                            : ''}
                                        {company.building_name ? `, ${company.building_name},` : ''} Singapore {company.postal_code}
                                    </Link>
                                </span>
                            </div>
                            <Divider />
                            <div className="flex flex-col">
                                <span className="text-black/80">Postal Code:</span>
                                <span className="text-sm">{company.postal_code}</span>
                            </div>
                            <Divider />
                            {renderContactInfo('Phone', contacts)}
                            {renderContactInfo('Email', emails)}
                            {renderContactInfo('Website', websites)}
                            {renderSocialMediaIcons(social_medias)}
                        </div>
                </CardBody>
            </Card>
    );
};

export default ContactInformation;
